/** Default row factories mirroring BinTemperatureSystem Setup.xaml.cs add helpers. */

import {
    btsFlatHouseCableTopPointXZ,
    btsFlatHouseOverrideFromTopPoint,
    flatHouseGridFromCableLayout,
    parseFlatCableLayoutGrid,
} from "./siteLayoutStice.js";
import {
    prepareTasksForSave,
} from "./siteSetupTasks.js";

// Heltec = LoRa gateway device. The Site Setup save triggers HeltecDeviceConfigData on the server to rebuild
// HeltecSensorMapping rows and push an apply_config downlink via ChirpStack; the gateway EUI is stored in
// Device.DeviceEui (nullable; populated only for Heltec rows). mqtt_collector handles non-Heltec gateways.
export const DEVICE_TYPES = ["Banner", "Terminator", "SDA", "UMDIO", "BRX", "Heltec"];
export const BIN_TYPES = ["Concrete", "Steel", "Flat House", "Ground Pile"];
/** BTS Module Setup → ModuleType combo (Setup.xaml.cs). */
export const MODULE_TYPES = ["DWI", "DO", "DI", "TMP", "HUM", "SDA"];
/** BTS Section Setup → SectionType combo (Setup.xaml.cs). Heltec sections map to a Heltec gateway device. */
export const SECTION_TYPES = ["Banner", "Rolfes", "SDA", "Terminator", "UMDIO", "BRX", "Heltec"];

const DEFAULT_SENSOR_TEMPERATURE = "72";

function isMissingSensorTemperature(value) {
    const tempStr = String(value ?? "").trim();
    return tempStr === "" || tempStr === "E" || tempStr === "-E-" || tempStr === "-O-";
}

function normalizeSensorTemperaturesForSave(sensors) {
    return (sensors ?? []).map((sensor) => {
        if (isHumiditySensor(sensor)) return sensor;
        const needsTemp = isMissingSensorTemperature(sensor.temperature);
        const needsLast = isMissingSensorTemperature(sensor.lastTemperature);
        if (!needsTemp && !needsLast) return sensor;
        return {
            ...sensor,
            ...(needsTemp ? { temperature: DEFAULT_SENSOR_TEMPERATURE } : {}),
            ...(needsLast ? { lastTemperature: DEFAULT_SENSOR_TEMPERATURE } : {}),
        };
    });
}

const MODULE_MODEL_OPTIONS = {
    DWI: [
        "420 Input 8",
        "420 Input 16",
        "Resistance Input 8",
        "Resistance Input 16",
        "TC Input 8",
        "TC Input 16",
    ],
    DO: [
        "420 Relay 8",
        "420 Relay 16",
        "Resistance Relay 8",
        "Resistance Relay 16",
        "TC Relay 8",
        "TC Relay 16",
    ],
    DI: ["Discrete Input 8", "Discrete Input 16"],
    TMP: ["Banner-TMP"],
    HUM: ["Banner-HUM"],
    SDA: ["SDA"],
};

const ALL_MODULE_MODEL_OPTIONS = [
    ...MODULE_MODEL_OPTIONS.DI,
    ...MODULE_MODEL_OPTIONS.DWI,
    ...MODULE_MODEL_OPTIONS.DO,
    "Banner-HUM",
    "Banner-TMP",
    "SDA",
];

/** Loose id match (API JSON may mix number/string ids). */
export function idsMatch(a, b) {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return String(a) === String(b);
}

export function sameBinId(a, b) {
    return idsMatch(a, b);
}

/** Banner humidity probes (BTS: Humidity, Banner-A, Banner-NA) are not layout-managed temp sensors. */
export function isHumiditySensor(sensor) {
    const t = String(sensor?.type ?? "").trim().toLowerCase();
    return (
        t === "humidity" ||
        t === "banner-a" ||
        t === "banner-na" ||
        t === "banner-hum"
    );
}

function isHumidityOnlyCable(cable, sensors) {
    const onCable = (sensors ?? []).filter((s) => idsMatch(s.cableId, cable?.id));
    if (onCable.length > 0) return onCable.every(isHumiditySensor);
    const cableName = String(cable?.name ?? "").trim();
    if (!cableName) return false;
    return (sensors ?? []).some(
        (s) => isHumiditySensor(s) && String(s.cableName ?? "").trim() === cableName
    );
}

function fanBelongsToBin(fan, bin) {
    if (sameBinId(fan?.binId, bin?.binId) && Number(bin?.binId) !== 0 && Number(fan?.binId) !== 0) {
        return true;
    }
    const fanBinName = String(fan?.binName ?? "").trim();
    const binName = String(bin?.name ?? "").trim();
    return Boolean(fanBinName && binName && fanBinName.toLowerCase() === binName.toLowerCase());
}

function resolveFanBin(fan, bins) {
    const binList = bins ?? [];
    const byId = binList.find((b) =>
        sameBinId(fan?.binId, b.binId) && Number(b.binId) !== 0 && Number(fan?.binId) !== 0
    );
    if (byId) return byId;
    const fanBinName = String(fan?.binName ?? "").trim().toLowerCase();
    if (!fanBinName) return null;
    return binList.find((b) => String(b.name ?? "").trim().toLowerCase() === fanBinName) ?? null;
}

function attachFansToBins(fans, bins) {
    const binList = bins ?? [];
    return (fans ?? []).map((fan) => {
        const bin = resolveFanBin(fan, binList);
        return bin ? { ...fan, binId: bin.binId, binName: bin.name } : fan;
    }).filter((fan) => resolveFanBin(fan, binList));
}

export function updateBinFanCounts(bins, fans) {
    return (bins ?? []).map((b) => ({
        ...b,
        fans: (fans ?? []).filter((f) => fanBelongsToBin(f, b)).length,
    }));
}

function humiditySensorBelongsToSite(sensor, bins, cables) {
    if (bins.some((b) => sameBinId(sensor.binId, b.binId))) return true;
    const binName = String(sensor.binName ?? "").trim();
    if (binName && bins.some((b) => String(b.name ?? "").trim() === binName)) return true;
    if (sensor.cableId == null || sensor.cableId === 0) return false;
    return (cables ?? []).some((c) => idsMatch(c.id, sensor.cableId));
}

function attachHumiditySensorToBin(sensor, bins) {
    const bin = bins.find((b) => sameBinId(sensor.binId, b.binId))
        ?? bins.find((b) => String(b.name ?? "").trim() === String(sensor.binName ?? "").trim());
    return bin ? { ...sensor, binId: bin.binId, binName: bin.name } : sensor;
}

/** Module model options for the Type column (BTS binds Module.Type). */
export function getModuleModelOptions(moduleType, deviceType) {
    const mt = (moduleType ?? "").toUpperCase();
    if (mt === "DWI") return MODULE_MODEL_OPTIONS.DWI;
    if (mt === "DO") return MODULE_MODEL_OPTIONS.DO;
    if (mt === "DI") return MODULE_MODEL_OPTIONS.DI;
    if (mt === "TMP") return MODULE_MODEL_OPTIONS.TMP;
    if (mt === "HUM") return MODULE_MODEL_OPTIONS.HUM;
    if (mt === "SDA" || (deviceType ?? "").toUpperCase() === "SDA") return MODULE_MODEL_OPTIONS.SDA;
    return ALL_MODULE_MODEL_OPTIONS;
}

function moduleUomForType(moduleType) {
    const mt = (moduleType ?? "").toUpperCase();
    if (mt === "DWI") return "Word";
    if (mt === "DO" || mt === "DI") return "Bit";
    return "";
}

/** Apply BTS ModuleType change (updates ModuleUom and valid Type). */
export function applyModuleTypeChange(module, moduleType, deviceType) {
    const options = getModuleModelOptions(moduleType, deviceType);
    const current = module.type || module.model || "";
    const nextType = options.includes(current) ? current : (options[0] ?? "");
    return {
        ...module,
        moduleType,
        moduleUom: moduleUomForType(moduleType),
        type: nextType,
    };
}

export function createDefaultDevice(number, copyFrom) {
    const base = copyFrom ?? {};
    return {
        uniqueId: base.uniqueId != null ? base.uniqueId - 1 : -number,
        id: base.id ?? 0,
        number,
        type: base.type ?? "Terminator",
        ipAddress: base.ipAddress ?? "192.168.1.1",
        modules: base.modules ?? 0,
        sections: base.sections ?? 0,
        hasPowerReset: base.hasPowerReset ?? false,
        hasResistanceCheck: base.hasResistanceCheck ?? false,
        hasHumiditySensor: base.hasHumiditySensor ?? false,
        isAmbientDevice: base.isAmbientDevice ?? false,
        isHumidityDevice: base.isHumidityDevice ?? false,
        name: base.name ?? `Device ${number}`,
        model: base.model ?? "",
        macAddress: base.macAddress ?? "",
        description: base.description ?? "",
        version: base.version ?? "",
        booter: base.booter ?? "",
        meterCorrection: base.meterCorrection ?? 0,
        online: false,
        connected: false,
        relativeHumidity: 0,
        ambientTemperature: 0,
        transportStructure: 0,
        ipPort: 0,
        comPort: "",
        lastRelativeHumidity: 0,
        lastAmbientTemperature: 0,
        lastUpdated: new Date().toISOString(),
        alarmOn: false,
        portName: "",
        ambientTemperatureError: "",
        lastWeatherStationAlertSent: new Date().toISOString(),
        relativeHumidityError: "",
        // Heltec gateway EUI (16 hex chars, e.g. "0604a7ac0000d0be"). Only used when type === "Heltec";
        // null for every other device type. HeltecDeviceConfigData writes this to HeltecSensorMapping
        // GatewayEui/DeviceEui and uses it as the ChirpStack downlink target for apply_config.
        deviceEui: base.deviceEui ?? null,
    };
}

export function createDefaultModule(slot, copyFrom, device) {
    const base = copyFrom ?? {};
    return {
        id: base.id != null ? base.id - 1 : -slot,
        model: base.model ?? "",
        ipAddress: device?.ipAddress ?? base.ipAddress ?? "",
        slot,
        moduleType: base.moduleType ?? "",
        moduleUom: base.moduleUom ?? "",
        startAt: base.startAt ?? null,
        dataLength: base.dataLength ?? null,
        hasHumiditySensor: base.hasHumiditySensor ?? false,
        isRo1: base.isRo1 ?? false,
        isRo2: base.isRo2 ?? false,
        isResistanceModule: base.isResistanceModule ?? false,
        deviceUniqueId: device?.uniqueId ?? base.deviceUniqueId ?? 0,
        hasAmbientTemperatureSensor: base.hasAmbientTemperatureSensor ?? false,
        ambientTemperatureSensorPosition: base.ambientTemperatureSensorPosition ?? 0,
        humiditySensorPosition: base.humiditySensorPosition ?? 0,
        resistanceSensorPosition: base.resistanceSensorPosition ?? 0,
        type: base.type ?? "",
        batteryLife: base.batteryLife ?? null,
        lastUpdated: new Date().toISOString(),
        alarmOn: false,
        isActive: true,
        humiditySensorVoltageRange: base.humiditySensorVoltageRange ?? "4-20",
        ambientTemperatureSensorVoltageRange: base.ambientTemperatureSensorVoltageRange ?? "",
        r1: 0, r2: 0, r3: 0, r4: 0, r5: 0, r6: 0, r7: 0, r8: 0,
        r9: 0, r10: 0, r11: 0, r12: 0, r13: 0, r14: 0, r15: 0, r16: 0,
        r17: 0, r18: 0, r19: 0, r20: 0, r21: 0, r22: 0, r23: 0, r24: 0,
        r25: 0, r26: 0, r27: 0, r28: 0, r29: 0, r30: 0, r31: 0, r32: 0,
    };
}

export function createDefaultSection(number, copyFrom, device) {
    const base = copyFrom ?? {};
    return {
        id: base.id != null ? base.id - 1 : -number,
        number,
        name: base.name ?? `Section ${number}`,
        ipAddress: device?.ipAddress ?? base.ipAddress ?? "",
        deviceUniqueId: device?.uniqueId ?? base.deviceUniqueId ?? 0,
        sectionType: base.sectionType ?? "Terminator",
        slots: base.slots ?? 6,
        cablesPerSlot: base.cablesPerSlot ?? 1,
        slotsUsed: base.slotsUsed ?? base.slots ?? 6,
        slotsOpen: base.slotsOpen ?? 0,
        meterCorrection: base.meterCorrection ?? 0,
        isBeingUsed: base.isBeingUsed ?? false,
        sectionIndex: base.sectionIndex ?? 0,
        isExpanded: false,
        isEnabled: true,
        connectedToBox: false,
        timeBetweenReads: base.timeBetweenReads ?? 0,
        boxId: device?.uniqueId ?? base.boxId ?? 0,
        lastRead: new Date().toISOString(),
    };
}

export function createDefaultBin(number, copyFrom) {
    const base = copyFrom ?? {};
    return {
        binId: copyFrom ? (copyFrom.binId < 0 ? copyFrom.binId - 1 : -1) : -1,
        number,
        name: base.name ? incrementName(base.name) : `Bin ${number}`,
        type: base.type ?? "Steel",
        typeId: base.typeId ?? 2,
        fans: 0,
        cables: 0,
        diameter: base.diameter ?? 36,
        height: base.height ?? 80,
        peakHeight: base.peakHeight ?? 85,
        grainWallHeight: base.grainWallHeight ?? 80,
        grainPeakHeight: base.grainPeakHeight ?? 85,
        cableLayout: base.cableLayout ?? "1",
        bottleCap: base.bottleCap ?? 4,
        angle: base.angle ?? 45,
        length: base.length ?? 100,
        width: base.width ?? 80,
        peakLength: base.peakLength ?? base.length ?? 100,
        grainType: base.grainType ?? "Empty",
        grainTypeId: 0,
        positionX: 0,
        positionY: 0,
        positionZ: 0,
        fontSize: 1,
        transparency3D: 0.35,
        degreesToNorth: 0,
        sections: 0,
        isExpanded: false,
        isSelected: true,
        floorType: "Flat",
        cableStartDirection: "TL",
        bushelsPerCubicFoot: 0.803563913,
        angleOfRepose: 23,
        usePercentFull: true,
        grainContour: "Unlevel",
        feetOfAirSpace: 1,
        fieldOfView: 45,
        lookDirectionY: -1,
        upDirectionX: -1,
        axisY: -1,
        highTemperatureLimit: 140,
        lowTemperatureLimit: -20,
        tgm: 12.5,
        lowTarget: 12,
        highTarget: 13,
        lastEmcHistory: new Date().toISOString(),
    };
}

export function createDefaultFan(number, copyFrom, bin, device) {
    const base = copyFrom ?? {};
    return {
        fanId: base.fanId != null ? base.fanId - 1 : -number,
        fanNumber: number,
        fanName: base.fanName ? incrementName(base.fanName) : `Fan ${number}`,
        binId: bin?.binId ?? 0,
        binName: bin?.name ?? "",
        ipAddress: device?.ipAddress ?? base.ipAddress ?? "",
        deviceUniqueId: device?.uniqueId ?? base.deviceUniqueId ?? 0,
        relayModule: base.relayModule ?? 0,
        relayTerminal: base.relayTerminal ?? 0,
        ampModule: base.ampModule ?? 0,
        ampTerminal: base.ampTerminal ?? 0,
        discreteModule: base.discreteModule ?? 0,
        discreteTerminal: base.discreteTerminal ?? 0,
        hasTransducer: base.hasTransducer ?? false,
        fanType: base.fanType ?? "",
        isOn: false,
        isActive: true,
        delay: 0,
        lastRunTime: 0,
        averageRunTime: 0,
        cycleRunTime: 0,
        isOnAutomatic: false,
        amperage: 0,
        deviceId: 0,
        registerAddress: 0,
        relayRegisterAddress: 0,
        ampRegisterAddress: 0,
        startRelayDelay: 0,
        stopRelayDelay: 0,
        fanOnAmpMin: 0,
        relayDelay: 0,
        fanPosition: 0,
        fanSide: "",
        fanRefresh: 0,
        dailyRunTime: 0,
        backlog: 0,
        highTargetWindow: 0,
        lowTargetWindow: 0,
        ampNormalMax: 0,
        ampNormalMin: 0,
        lastAmperage: 0,
        batteryLife: 0,
        onOffChanged: false,
        turnOn: false,
        turnOff: false,
        alarmOn: false,
        lastUpdated: new Date().toISOString(),
        highTemperatureWindow: 0,
        lowTemperatureWindow: 0,
        fanOnTerminator: "",
        autoStartHours: 0,
        autoStartTime: new Date().toISOString(),
        outputModuleTerminal: 0,
        discreteRegisterAddress: 0,
        fanAction: "",
        fanIssue: "",
        hasEmailedFanIssue: false,
        moistureThresholdOverride: 0,
        temperatureThresholdOverride: 0,
        fanBinPosition: 0,
        module: 0,
        outputModule: 0,
        terminal: 0,
    };
}

export function createDefaultCable(number, copyFrom, bin) {
    const base = copyFrom ?? {};
    return {
        id: base.id != null ? base.id - 1 : -number,
        number,
        name: base.name ? incrementName(base.name) : `Cable ${number}`,
        cableType: base.cableType ?? "BRX",
        binId: bin?.binId ?? 0,
        binName: bin?.name ?? "",
        sectionId: 0,
        sectionName: "",
        sensorCount: base.sensorCount ?? 0,
        sensorSpacing: base.sensorSpacing ?? 6,
        firstSensorHeight: base.firstSensorHeight ?? 1,
        position: base.position ?? 0,
        sectionPosition: base.sectionPosition ?? 0,
        sectionPositionStartAt: base.sectionPositionStartAt ?? 0,
        ipAddress: base.ipAddress ?? "",
        deviceUniqueId: base.deviceUniqueId ?? 0,
        aframeId: base.aframeId ?? 0,
        aframe: base.aframe ?? "",
        aframePosition: base.aframePosition ?? 0,
        dateRead: new Date().toISOString(),
        isExpanded: false,
        timeToRead: 0,
        bottomPointX: 0,
        bottomPointY: 0,
        bottomPointZ: 0,
        topPointX: 0,
        topPointY: 0,
        topPointZ: 0,
        firstBannerSensorHeight: 0,
        bannerSensorSpacing: 0,
        hardware: "",
        percentFunctional: 0,
        leadWireLength: 0,
        grainLevelPosition: 0,
        overrideGrainLevelPosition: false,
        isPositionOverride: base.isPositionOverride ?? false,
        isOffsetFromOrigin: base.isOffsetFromOrigin ?? false,
        overrideOffsetX: base.overrideOffsetX ?? 0,
        overrideOffsetY: base.overrideOffsetY ?? 0,
        overrideOffsetZ: base.overrideOffsetZ ?? 0,
        anglePosition: base.anglePosition ?? 0,
    };
}

const BTS_SENSOR_TYPES = ["BRX", "Terminator", "Temp", "Humidity", "Banner-A", "Banner-NA", "Banner-HUM", "Banner-TMP"];

/** Sensor.Type and Cable.CableType are varchar(10). "Temperature" does not fit and is not a BTS type. */
function fitBtsSensorType(sensorType, cableType) {
    const sensor = String(sensorType ?? "").trim();
    if (BTS_SENSOR_TYPES.includes(sensor)) return sensor;
    const cable = String(cableType ?? "").trim();
    if (BTS_SENSOR_TYPES.includes(cable)) return cable;
    return "BRX";
}

export function createDefaultSensor(number, copyFrom, cable, bin) {
    const base = copyFrom ?? {};
    return {
        id: base.id != null ? base.id - 1 : -number,
        number,
        position: number,
        name: cable?.name ? `${cable.name}-${number}` : `Sensor ${number}`,
        type: fitBtsSensorType(base.type, cable?.cableType),
        binId: bin?.binId ?? cable?.binId ?? 0,
        binName: bin?.name ?? cable?.binName ?? "",
        cableId: cable?.id ?? 0,
        cableName: cable?.name ?? "",
        alternateCableId: cable?.id ?? 0,
        alternateCableName: cable?.name ?? "",
        deviceIp: base.deviceIp ?? "",
        deviceId: base.deviceId ?? 0,
        deviceUniqueId: base.deviceUniqueId ?? 0,
        sectionId: base.sectionId ?? 0,
        sectionName: base.sectionName ?? "",
        temperature: DEFAULT_SENSOR_TEMPERATURE,
        resistance: "",
        humidity: "",
        forcedOpen: false,
        isActive: true,
        isExpanded: false,
        highTemperatureLimit: 140,
        lowTemperatureLimit: -20,
        highHumidityLimit: 0,
        lowHumidityLimit: 0,
        isAtLimit: false,
        checks: 0,
        reads: 0,
        lastTemperature: DEFAULT_SENSOR_TEMPERATURE,
        lastResistance: "",
        lastHumidity: "",
        emc: 0,
        mcCalibration: 0,
        batteryLife: 0,
        sdaPosition: 0,
        lastMcHistoryUpdate: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        humidityRegisterAddress: 0,
        temperatureRegisterAddress: 0,
    };
}

function incrementName(name) {
    const m = String(name).match(/^(.*?)(\d+)$/);
    if (!m) return `${name} 2`;
    return `${m[1]}${Number(m[2]) + 1}`;
}

export function resizeList(list, targetCount, createRow) {
    const next = [...(list ?? [])];
    while (next.length > targetCount) next.pop();
    while (next.length < targetCount) {
        next.push(createRow(next.length + 1, next[next.length - 1]));
    }
    return next.map((row, i) => ({ ...row, number: row.number ?? i + 1 }));
}

export function renumberBins(bins) {
    return (bins ?? []).map((b, i) => ({ ...b, number: i + 1 }));
}

export function renumberDevices(devices) {
    return (devices ?? []).map((d, i) => ({ ...d, number: i + 1 }));
}

function orderedBins(bins) {
    return [...(bins ?? [])].sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
}

/** Per-bin cable order (BTS assign uses bin order then cable number within each bin). */
function perBinCableSortKey(cable) {
    const pos = Number(cable?.position);
    if (Number.isFinite(pos) && pos > 0) return pos;
    const nameMatch = String(cable?.name ?? "").match(/-(\d+)$/);
    if (nameMatch) return Number(nameMatch[1]);
    return Number(cable?.number) || 0;
}

/** Cables for one bin in display/save order (BTS DrawCables sorts by Number). */
function cablesForBin(cables, binId) {
    return (cables ?? [])
        .filter((c) => sameBinId(c.binId, binId))
        .sort((a, b) => {
            const na = perBinCableSortKey(a);
            const nb = perBinCableSortKey(b);
            if (na !== nb) return na - nb;
            return (a.id ?? 0) - (b.id ?? 0);
        });
}

/** Sum round-bin layer string (e.g. "0-4-8-12" → 24). Flat house `3-10` → 30 (3×10). */
export function cableCountFromLayout(cableLayout, binOrType) {
    if (cableLayout == null || cableLayout === "") return 0;
    const parts = String(cableLayout)
        .split("-")
        .map((p) => parseInt(p.trim(), 10))
        .filter((n) => Number.isFinite(n));
    if (!parts.length) return 0;

    if (parts.length === 2) {
        const label =
            typeof binOrType === "string"
                ? binOrType
                : `${binOrType?.type ?? ""} ${binOrType?.name ?? ""}`;
        if (/flat\s*house|flathouse/i.test(label)) {
            return parts[0] * parts[1];
        }
    }

    return parts.reduce((a, b) => a + b, 0);
}

/** BTS Figure Bin Layout — auto-generate cableLayout from cable count when empty or mismatched. */
export function figureBinCableLayout(bin) {
    const cables = Math.max(0, Number(bin?.cables) || 0);
    const layout = bin?.cableLayout == null ? "" : String(bin.cableLayout);
    const layoutSum = cableCountFromLayout(layout, bin);
    if (layout !== "" && layoutSum === cables) {
        return layout;
    }

    // Flat houses use 2-part grid strings (e.g. 3-10), not round-bin layer chains.
    if (/flat\s*house|flathouse/i.test(String(bin?.type ?? ""))) {
        const parts = layout
            .split("-")
            .map((p) => parseInt(p.trim(), 10))
            .filter((n) => Number.isFinite(n) && n > 0);
        if (parts.length === 2) return layout;
        return layout;
    }

    switch (cables) {
        case 0:
            return "0";
        case 1:
            return "1";
        case 2:
            return "0-2";
        case 3:
            return "0-3";
        default: {
            const layouts = [];
            let nextCables = cables;
            if (cables % 2 === 0) {
                layouts.push(0);
            } else {
                layouts.push(1);
                nextCables -= 1;
            }
            const increments = Math.floor(nextCables / 4);
            for (let i = 0; i < increments; i += 1) {
                const c = 4 * (i + 1);
                nextCables -= c;
                if (nextCables === 0) {
                    layouts.push(c);
                    break;
                }
                if (nextCables === c) {
                    layouts.push(c * 2);
                    break;
                }
                if (nextCables < c) {
                    if (nextCables < 0) {
                        layouts.push(c + nextCables);
                    } else if (nextCables < c + 4) {
                        layouts.push(c + nextCables);
                    }
                    break;
                }
                if (nextCables > c) {
                    layouts.push(c);
                }
            }
            return [...layouts].sort((a, b) => a - b).join("-");
        }
    }
}

export function applyBinCableLayouts(bins) {
    return (bins ?? []).map((bin) => ({
        ...bin,
        cableLayout: figureBinCableLayout(bin),
    }));
}

export function updateBinCableCounts(bins, cables, sensors) {
    return (bins ?? []).map((b) => ({
        ...b,
        cables: (cables ?? []).filter((c) =>
            sameBinId(c.binId, b.binId) && !isHumidityOnlyCable(c, sensors)
        ).length,
    }));
}

/**
 * Reconcile bins/cables/sensors/fans immediately before save so the first save
 * includes cable rows and sensors even when sections were never expanded.
 */

/** Site Setup edits selected Main email fields; server preserves other Main columns from BTS. */
export function prepareMainForSave(main) {
    if (!main) return null;
    const siteName = main.siteName ?? main.SiteName ?? "";
    const emailField = (camel, pascal) => String(main[camel] ?? main[pascal] ?? "");
    return {
        siteName,
        isAutoScanOn: !!(main.isAutoScanOn ?? main.IsAutoScanOn),
        emailReportTo: emailField("emailReportTo", "EmailReportTo"),
        emailAlertTo: emailField("emailAlertTo", "EmailAlertTo"),
        emailScreenshotTo: emailField("emailScreenshotTo", "EmailScreenshotTo"),
        emailLastTemperatureReportTo: emailField("emailLastTemperatureReportTo", "EmailLastTemperatureReportTo"),
    };
}

function isModuleSetupFieldDefined(value) {
    return value !== "" && value != null && value !== undefined;
}

function normalizeModuleVoltageRange(value, fallback) {
    const text = String(value ?? "").trim();
    if (text === "" || text === "0") return fallback;
    return text;
}

function prepareModulesForSave(modules) {
    return (modules ?? []).map((module) => {
        const startAtDefined = isModuleSetupFieldDefined(module.startAt);
        const dataLengthDefined = isModuleSetupFieldDefined(module.dataLength);
        const batteryLifeDefined = isModuleSetupFieldDefined(module.batteryLife);
        return {
            ...module,
            startAt: startAtDefined ? Number(module.startAt) : 0,
            dataLength: dataLengthDefined ? Number(module.dataLength) : 0,
            batteryLife: batteryLifeDefined ? Number(module.batteryLife) : 0,
            humiditySensorVoltageRange: normalizeModuleVoltageRange(module.humiditySensorVoltageRange, "4-20"),
            ambientTemperatureSensorVoltageRange: normalizeModuleVoltageRange(
                module.ambientTemperatureSensorVoltageRange,
                ""
            ),
            startAtDefined,
            dataLengthDefined,
            batteryLifeDefined,
        };
    });
}

export function prepareSetupForSave(setup) {
    if (!setup) return setup;

    let bins = [...(setup.bins ?? [])];
    let cables = [...(setup.cables ?? [])];
    let sensors = [...(setup.sensors ?? [])];

    bins = bins.map((b) => {
        const fromLayout = cableCountFromLayout(b.cableLayout, b);
        const current = Number(b.cables) || 0;
        if (fromLayout > 0 && fromLayout !== current) {
            return { ...b, cables: fromLayout };
        }
        if (current > 0) return b;
        return fromLayout > 0 ? { ...b, cables: fromLayout } : b;
    });

    bins = bins.map((b) =>
        binLooksLikeFlatHouse(b)
            ? { ...b, cableLayout: repairFlatHouseCableLayout(b) }
            : b
    );
    bins = applyBinCableLayouts(bins);
    bins = normalizeFlatHouseBinsForSave(bins);

    bins = updateBinCableCounts(bins, cables, sensors);

    const cableSync = syncCablesFromBins(bins, cables, setup.sections, sensors);
    cables = cableSync.cables;
    sensors = normalizeSensorTemperaturesForSave(cableSync.sensors);
    bins = updateBinCableCounts(bins, cables, sensors);
    cables = applyFlatHouseCableTopPointsForSave(cables, bins);

    const sectionPositionRefs = syncAllSectionPositionRefs(
        setup.sections,
        setup.sectionPositionRefs
    );

    const fansAttached = attachFansToBins(setup.fans, bins);
    bins = updateBinFanCounts(bins, fansAttached);
    const fans = syncFansFromBins(bins, fansAttached, setup.devices);
    const modules = prepareModulesForSave(setup.modules);
    const merged = {
        ...setup,
        bins,
        cables,
        sensors,
        fans,
        modules,
        sectionPositionRefs,
    };

    const tasks = prepareTasksForSave(merged);
    return ensureUniqueSetupIds({
        ...merged,
        tasks,
        // Explicit so an empty schedule clears Task rows instead of being treated as "not sent".
        tasksIncluded: true,
    });
}

function findRowInsertIndex(rows, binId, orderedBinList, matchKey = "binId") {
    const binIndex = orderedBinList.findIndex((b) => sameBinId(b.binId, binId));
    if (binIndex < 0) return rows.length;

    let lastIdx = -1;
    for (let i = 0; i < rows.length; i += 1) {
        if (sameBinId(rows[i][matchKey], binId)) lastIdx = i;
    }
    if (lastIdx >= 0) return lastIdx + 1;

    for (let i = 0; i < rows.length; i += 1) {
        const rowBinIdx = orderedBinList.findIndex((b) => sameBinId(b.binId, rows[i][matchKey]));
        if (rowBinIdx > binIndex) return i;
    }
    return rows.length;
}

function orderedDevices(devices) {
    return [...(devices ?? [])].sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
}

function findDeviceScopedInsertIndex(rows, deviceUniqueId, orderedDeviceList, matchKey = "deviceUniqueId") {
    const deviceIndex = orderedDeviceList.findIndex((d) => d.uniqueId === deviceUniqueId);
    if (deviceIndex < 0) return rows.length;

    let lastIdx = -1;
    for (let i = 0; i < rows.length; i += 1) {
        if (rows[i][matchKey] === deviceUniqueId) lastIdx = i;
    }
    if (lastIdx >= 0) return lastIdx + 1;

    for (let i = 0; i < rows.length; i += 1) {
        const rowDeviceIdx = orderedDeviceList.findIndex((d) => d.uniqueId === rows[i][matchKey]);
        if (rowDeviceIdx > deviceIndex) return i;
    }
    return rows.length;
}

/** BTS Setup: device.Modules drives module rows for that device (AddModules / RemoveModules). */
export function syncModulesFromDevices(devices, modules) {
    const deviceList = orderedDevices(devices);
    let nextModules = [...(modules ?? [])];
    const deviceIds = new Set(deviceList.map((d) => d.uniqueId));
    nextModules = nextModules.filter((m) => deviceIds.has(m.deviceUniqueId));

    for (const device of deviceList) {
        const target = Math.max(0, Number(device.modules) || 0);
        let deviceModules = nextModules.filter((m) => m.deviceUniqueId === device.uniqueId);

        while (deviceModules.length < target) {
            const copy = deviceModules.length > 0
                ? deviceModules[deviceModules.length - 1]
                : (nextModules.length > 0 ? nextModules[nextModules.length - 1] : null);
            const newModule = createDefaultModule(
                (copy?.slot ?? deviceModules.length) + 1,
                copy,
                device
            );
            const insertIdx = findDeviceScopedInsertIndex(nextModules, device.uniqueId, deviceList);
            nextModules.splice(insertIdx, 0, newModule);
            deviceModules = nextModules.filter((m) => m.deviceUniqueId === device.uniqueId);
        }

        while (deviceModules.length > target) {
            const toRemove = deviceModules[deviceModules.length - 1];
            nextModules = nextModules.filter((m) => m.id !== toRemove.id);
            deviceModules = nextModules.filter((m) => m.deviceUniqueId === device.uniqueId);
        }

        deviceModules = nextModules.filter((m) => m.deviceUniqueId === device.uniqueId);
        for (let i = 0; i < deviceModules.length; i += 1) {
            const idx = nextModules.findIndex((m) => m.id === deviceModules[i].id);
            if (idx >= 0) {
                nextModules[idx] = {
                    ...nextModules[idx],
                    slot: i + 1,
                    ipAddress: device.ipAddress,
                    deviceUniqueId: device.uniqueId,
                };
            }
        }
    }

    const deviceOrder = new Map(deviceList.map((d) => [d.uniqueId, d.number ?? 0]));
    return nextModules.sort((a, b) => {
        const da = deviceOrder.get(a.deviceUniqueId) ?? 9999;
        const db = deviceOrder.get(b.deviceUniqueId) ?? 9999;
        if (da !== db) return da - db;
        return (a.slot ?? 0) - (b.slot ?? 0);
    });
}

/** BTS ReorderSections — global section number + default name after add/remove. */
function renumberSectionsGlobally(sections, cables, sensors) {
    const nextSections = (sections ?? []).map((s, i) => ({
        ...s,
        number: i + 1,
        sectionIndex: i,
        name: `Section ${i + 1}`,
    }));
    const sectionById = new Map(nextSections.map((s) => [s.id, s]));
    const nextCables = (cables ?? []).map((c) => {
        if (!c.sectionId) return c;
        const sec = sectionById.get(c.sectionId);
        return sec ? { ...c, sectionName: sec.name } : c;
    });
    const nextSensors = (sensors ?? []).map((s) => {
        if (!s.sectionId) return s;
        const sec = sectionById.get(s.sectionId);
        return sec ? { ...s, sectionName: sec.name } : s;
    });
    return { sections: nextSections, cables: nextCables, sensors: nextSensors };
}

/** BTS Setup: device.Sections drives section rows for that device (AddSections / RemoveSections). */
export function syncSectionsFromDevices(devices, sections, sectionPositionRefs, cables, sensors) {
    const deviceList = orderedDevices(devices);
    let nextSections = [...(sections ?? [])];
    let nextRefs = [...(sectionPositionRefs ?? [])];
    let nextCables = [...(cables ?? [])];
    let nextSensors = [...(sensors ?? [])];
    const deviceIds = new Set(deviceList.map((d) => d.uniqueId));

    nextSections = nextSections.filter((s) => deviceIds.has(s.deviceUniqueId));
    nextRefs = nextRefs.filter((r) => nextSections.some((s) => idsMatch(s.id, r.sectionId)));

    for (const device of deviceList) {
        const target = Math.max(0, Number(device.sections) || 0);
        let deviceSections = nextSections.filter((s) => s.deviceUniqueId === device.uniqueId);

        while (deviceSections.length < target) {
            const copy = deviceSections.length > 0
                ? deviceSections[deviceSections.length - 1]
                : (nextSections.length > 0 ? nextSections[nextSections.length - 1] : null);
            const newSection = createDefaultSection(
                (copy?.number ?? nextSections.length) + 1,
                copy,
                device
            );
            const insertIdx = findDeviceScopedInsertIndex(nextSections, device.uniqueId, deviceList);
            nextSections.splice(insertIdx, 0, newSection);
            nextRefs = syncSectionPositionRefsForSection(newSection, nextRefs);
            deviceSections = nextSections.filter((s) => s.deviceUniqueId === device.uniqueId);
        }

        while (deviceSections.length > target) {
            const toRemove = deviceSections[deviceSections.length - 1];
            nextSections = nextSections.filter((s) => s.id !== toRemove.id);
            nextRefs = nextRefs.filter((r) => r.sectionId !== toRemove.id);
            nextCables = nextCables.map((c) => (
                idsMatch(c.sectionId, toRemove.id) ? { ...c, sectionId: 0, sectionName: "" } : c
            ));
            nextSensors = nextSensors.map((s) => (
                idsMatch(s.sectionId, toRemove.id) ? { ...s, sectionId: 0, sectionName: "" } : s
            ));
            deviceSections = nextSections.filter((s) => s.deviceUniqueId === device.uniqueId);
        }
    }

    nextSections = nextSections.map((s) => {
        const device = deviceList.find((d) => d.uniqueId === s.deviceUniqueId);
        return {
            ...s,
            ipAddress: device?.ipAddress ?? s.ipAddress,
            deviceUniqueId: s.deviceUniqueId,
            boxId: s.deviceUniqueId,
        };
    });

    const renumbered = renumberSectionsGlobally(nextSections, nextCables, nextSensors);
    nextSections = renumbered.sections;
    nextCables = renumbered.cables;
    nextSensors = renumbered.sensors;
    nextRefs = syncAllSectionPositionRefs(nextSections, nextRefs);
    nextRefs = sortSectionPositionRefs(nextSections, nextRefs);

    return {
        sections: nextSections,
        sectionPositionRefs: nextRefs,
        cables: nextCables,
        sensors: nextSensors,
    };
}

/** When a device IP changes, keep modules/sections/cables/sensors/fans aligned (BTS Setup). */
export function propagateDeviceIpAddress(
    devices,
    modules,
    sections,
    sectionPositionRefs,
    cables,
    sensors,
    fans,
    bins,
    deviceUniqueId,
    oldIp
) {
    const modulesNext = syncModulesFromDevices(devices, modules);
    const sectionSync = syncSectionsFromDevices(
        devices,
        sections,
        sectionPositionRefs,
        cables,
        sensors
    );
    const assigned = assignSectionsToCables(
        sectionSync.sections,
        sectionSync.sectionPositionRefs,
        bins,
        sectionSync.cables,
        sectionSync.sensors
    );

    const device = (devices ?? []).find((d) => d.uniqueId === deviceUniqueId);
    const newIp = device?.ipAddress ?? "";
    const deviceSectionIds = new Set(
        (sections ?? []).filter((s) => s.deviceUniqueId === deviceUniqueId).map((s) => s.id)
    );

    const ownedByDevice = (row) =>
        row.deviceUniqueId === deviceUniqueId
        || row.boxId === deviceUniqueId
        || (row.id != null && deviceSectionIds.has(row.id));

    const patchDeviceIp = (row, ipKey = "ipAddress") => {
        if (!newIp) return row;
        if (ownedByDevice(row)) {
            return { ...row, [ipKey]: newIp };
        }
        if (oldIp && row[ipKey] === oldIp && ownedByDevice(row)) {
            return { ...row, [ipKey]: newIp };
        }
        return row;
    };

    return {
        modules: modulesNext.map((m) => patchDeviceIp(m)),
        sections: sectionSync.sections.map((s) => patchDeviceIp(s)),
        sectionPositionRefs: sectionSync.sectionPositionRefs,
        cables: assigned.cables.map((c) => patchDeviceIp(c)),
        sensors: assigned.sensors.map((s) => {
            if (s.deviceUniqueId === deviceUniqueId) {
                return { ...s, deviceIp: newIp };
            }
            if (oldIp && s.deviceIp === oldIp && s.deviceUniqueId === deviceUniqueId) {
                return { ...s, deviceIp: newIp };
            }
            return s;
        }),
        fans: (fans ?? []).map((f) => patchDeviceIp(f)),
    };
}

/** Keep setup.cables aligned with each bin's Cables count (BTS AddCables / RemoveCables). */
export function syncCablesFromBins(bins, cables, sections, sensors) {
    const binList = orderedBins(bins);
    let nextCables = [...(cables ?? [])];
    let nextSensors = [...(sensors ?? [])];
    const humiditySensors = nextSensors.filter(isHumiditySensor);
    const humidityOnlyCables = nextCables.filter((c) => isHumidityOnlyCable(c, nextSensors));
    const humidityOnlyCableIds = new Set(humidityOnlyCables.map((c) => c.id));

    nextCables = nextCables.filter((c) => !humidityOnlyCableIds.has(c.id));
    nextSensors = nextSensors.filter((s) => !isHumiditySensor(s));

    nextCables = nextCables.filter((c) =>
        binList.some((b) => sameBinId(c.binId, b.binId))
    );
    const keptCableIds = new Set(nextCables.map((c) => c.id));
    nextSensors = nextSensors.filter((s) => keptCableIds.has(s.cableId));

    for (const bin of binList) {
        const target = Math.max(0, Number(bin.cables) || 0);
        let binCables = cablesForBin(nextCables, bin.binId);

        while (binCables.length < target) {
            const copy = binCables.length > 0 ? binCables[binCables.length - 1] : null;
            const newCable = createDefaultCable(binCables.length + 1, copy, bin);
            newCable.name = `${bin.name}-${binCables.length + 1}`;
            newCable.binName = bin.name;
            const insertIdx = findRowInsertIndex(nextCables, bin.binId, binList);
            nextCables.splice(insertIdx, 0, newCable);
            binCables = cablesForBin(nextCables, bin.binId);
        }

        while (binCables.length > target) {
            const toRemove = binCables[binCables.length - 1];
            nextCables = nextCables.filter((c) => c.id !== toRemove.id);
            nextSensors = nextSensors.filter((s) => s.cableId !== toRemove.id);
            binCables = cablesForBin(nextCables, bin.binId);
        }
    }

    let cableNumber = 1;
    const renumberedCables = [];
    for (const bin of binList) {
        let binCablePosition = 1;
        for (const cable of cablesForBin(nextCables, bin.binId)) {
            renumberedCables.push({
                ...cable,
                number: cableNumber,
                position: binCablePosition,
                binName: bin.name,
                name: `${bin.name}-${binCablePosition}`,
            });
            cableNumber += 1;
            binCablePosition += 1;
        }
        for (const humidityCable of humidityOnlyCables.filter((c) =>
            sameBinId(c.binId, bin.binId)
            || String(c.binName ?? "").trim() === String(bin.name ?? "").trim()
        )) {
            renumberedCables.push({
                ...humidityCable,
                number: cableNumber,
                binId: bin.binId,
                binName: bin.name,
            });
            cableNumber += 1;
        }
    }

    return {
        cables: renumberedCables,
        sensors: syncSensorsFromCables(
            renumberedCables,
            [...nextSensors, ...humiditySensors],
            binList
        ),
    };
}

/** Keep setup.fans aligned with each bin's Fans count (BTS AddFans / RemoveFans). */
export function syncFansFromBins(bins, fans, devices) {
    const binList = orderedBins(bins);
    let nextFans = attachFansToBins(fans, binList);

    for (const bin of binList) {
        const target = Math.max(0, Number(bin.fans) || 0);
        let binFans = nextFans.filter((f) => sameBinId(f.binId, bin.binId));
        const device = devices?.find((d) => d.isAmbientDevice) ?? devices?.[0];

        while (binFans.length < target) {
            const copy = binFans.length > 0 ? binFans[binFans.length - 1] : null;
            const newFan = createDefaultFan(binFans.length + 1, copy, bin, device);
            newFan.fanName = `${bin.name}-${binFans.length + 1}`;
            newFan.binName = bin.name;
            const insertIdx = findRowInsertIndex(nextFans, bin.binId, binList);
            nextFans.splice(insertIdx, 0, newFan);
            binFans = nextFans.filter((f) => sameBinId(f.binId, bin.binId));
        }

        while (binFans.length > target) {
            const toRemove = binFans[0];
            nextFans = nextFans.filter((f) => f.fanId !== toRemove.fanId);
            binFans = nextFans.filter((f) => sameBinId(f.binId, bin.binId));
        }
    }

    let fanNumber = 1;
    const renumberedFans = [];
    for (const bin of binList) {
        for (const fan of nextFans.filter((f) => sameBinId(f.binId, bin.binId))) {
            renumberedFans.push({
                ...fan,
                fanNumber,
                binName: bin.name,
                fanName: fan.fanName || `${bin.name}-${fanNumber}`,
            });
            fanNumber += 1;
        }
    }
    return renumberedFans;
}

const binLooksLikeFlatHouse = (bin) =>
    /flat\s*house|flathouse/i.test(
        `${bin?.type ?? ""} ${bin?.name ?? ""}`
    );

/** Fix MSSQL date-corrupted layouts (`3/10/2026`) so flat-house cable math runs on save. */
export function repairFlatHouseCableLayout(bin) {
    const layout = String(bin?.cableLayout ?? "").trim();
    if (parseFlatCableLayoutGrid(layout)) return layout;

    const slash = layout.match(/^(\d{1,2})\/(\d{1,2})\/\d{2,4}$/);
    if (slash) return `${slash[1]}-${slash[2]}`;

    const isoDate = layout.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoDate) return `${Number(isoDate[2])}-${Number(isoDate[3])}`;

    const cables = Number(bin?.cables) || 0;
    if (cables === 30) return "3-10";

    const fromFigure = figureBinCableLayout(bin);
    if (parseFlatCableLayoutGrid(fromFigure)) return fromFigure;
    return layout;
}

/**
 * BTS flat-house footprint: Width runs on X, Length on Z, and House3D pins the roof ridge to
 * the Length axis (peak points at Z = ±PeakLength/2) with PeakLength ≤ Length. Rows stored long
 * on Width therefore render a gable across the short side — the ridge looks turned 90°.
 *
 * Normalize to BTS's own convention (Length = long side, PeakLength = Length). Since
 * FlatHouse3D never rotates the mesh and only spins the N/W/S/E labels, the quarter turn has to
 * be carried by DegreesToNorth (+90°) so the compass, doors, and fan sides stay where they were.
 */
export function normalizeFlatHouseBinsForSave(bins) {
    return (bins ?? []).map((bin) => {
        if (!binLooksLikeFlatHouse(bin)) return bin;
        const next = {
            ...bin,
            cableLayout: repairFlatHouseCableLayout(bin),
        };

        let width = Number(next.width) || 0;
        let length = Number(next.length) || 0;
        if (width > 0 && length > 0 && width > length) {
            next.width = length;
            next.length = width;
            next.degreesToNorth =
                (((Number(next.degreesToNorth) || 0) + 90) % 360 + 360) % 360;
            length = next.length;
        }

        if (length > 0) {
            next.peakLength = length;
        }
        if (next.cableStartDirection !== "BL") {
            next.cableStartDirection = "BL";
        }
        return next;
    });
}

/** Write BTS Cable top points + FlatHouse3D position overrides before cloud save. */
export function applyFlatHouseCableTopPointsForSave(cables, bins) {
    const binList = orderedBins(bins);
    return (cables ?? []).map((cable) => {
        const bin = binList.find((b) => sameBinId(cable.binId, b.binId));
        if (!bin || !binLooksLikeFlatHouse(bin)) return cable;
        if (!parseFlatCableLayoutGrid(bin.cableLayout)) return cable;
        const binCableCount = (cables ?? []).filter((c) =>
            sameBinId(c.binId, bin.binId)
        ).length;
        if (
            !flatHouseGridFromCableLayout(
                bin.cableLayout,
                bin.width,
                bin.length,
                binCableCount || bin?.cableList?.length
            )
        ) {
            return cable;
        }

        const cableNum =
            Number(cable.position) > 0
                ? Number(cable.position)
                : Number(cable.number) || 0;

        const xz = btsFlatHouseCableTopPointXZ(cableNum, bin);
        if (!xz) return cable;

        // BTS FlatHouse3D only honors TopPoint via IsOffsetFromOrigin=true overrides
        // (Wpf3D: X = StartCablePoint.X - OverrideOffsetX, Z = Start + OverrideOffsetZ).
        // IsOffsetFromOrigin=false uses bunk layout and ignores TopPointX/Z entirely.
        const overrides = btsFlatHouseOverrideFromTopPoint(
            bin,
            xz.topPointX,
            xz.topPointZ
        );
        if (!overrides) return cable;

        const wallHeight = Number(bin.height) || Number(bin.diameter) || 35;
        const bottomY = -wallHeight / 2;
        const sensorCount = Math.max(1, Number(cable.sensorCount) || 6);
        const spacing = Number(cable.sensorSpacing) || 6;
        const firstHeight =
            Number(cable.firstSensorHeight) > 0
                ? Number(cable.firstSensorHeight)
                : 3;
        const topY =
            bottomY +
            firstHeight +
            Math.max(0, sensorCount - 1) * spacing;

        return {
            ...cable,
            cableType: cable.cableType || "BRX",
            bottomPointX: xz.topPointX,
            bottomPointY: bottomY,
            bottomPointZ: xz.topPointZ,
            topPointX: xz.topPointX,
            topPointY: topY,
            topPointZ: xz.topPointZ,
            anglePosition: cableNum,
            isPositionOverride: true,
            isOffsetFromOrigin: true,
            overrideOffsetX: overrides.overrideOffsetX,
            overrideOffsetY: 0,
            overrideOffsetZ: overrides.overrideOffsetZ,
        };
    });
}

function findSensorInsertIndex(sensors, cable, orderedCables) {
    const cableIndex = orderedCables.findIndex((c) => c.id === cable.id);
    if (cableIndex < 0) return sensors.length;

    let lastIdx = -1;
    for (let i = 0; i < sensors.length; i += 1) {
        if (sensors[i].cableId === cable.id) lastIdx = i;
    }
    if (lastIdx >= 0) return lastIdx + 1;

    for (let i = 0; i < sensors.length; i += 1) {
        const sensorCableIdx = orderedCables.findIndex((c) => c.id === sensors[i].cableId);
        if (sensorCableIdx > cableIndex) return i;
    }
    return sensors.length;
}

/** Keep setup.sensors aligned with each cable's Sens count (BTS AddSensors / RemoveSensors). */
export function syncSensorsFromCables(cables, sensors, bins) {
    const cableList = [...(cables ?? [])];
    const binList = orderedBins(bins);
    const humiditySensors = (sensors ?? []).filter(isHumiditySensor);
    let nextSensors = (sensors ?? []).filter((s) => !isHumiditySensor(s));
    const cableIds = new Set(cableList.map((c) => c.id));
    nextSensors = nextSensors.filter((s) => cableIds.has(s.cableId));

    for (const cable of cableList) {
        if (isHumidityOnlyCable(cable, sensors)) continue;
        const target = Math.max(0, Number(cable.sensorCount) || 0);
        let cableSensors = nextSensors.filter((s) => s.cableId === cable.id);
        const bin = binList.find((b) => sameBinId(b.binId, cable.binId));

        while (cableSensors.length < target) {
            const copy = cableSensors.length > 0 ? cableSensors[cableSensors.length - 1] : null;
            const newSensor = createDefaultSensor(cableSensors.length + 1, copy, cable, bin);
            const insertIdx = findSensorInsertIndex(nextSensors, cable, cableList);
            nextSensors.splice(insertIdx, 0, newSensor);
            cableSensors = nextSensors.filter((s) => s.cableId === cable.id);
        }

        while (cableSensors.length > target) {
            const toRemove = cableSensors[0];
            nextSensors = nextSensors.filter((s) => s.id !== toRemove.id);
            cableSensors = nextSensors.filter((s) => s.cableId === cable.id);
        }
    }

    let sensorNumber = 1;
    const renumberedSensors = [];
    for (const cable of cableList) {
        if (isHumidityOnlyCable(cable, sensors)) continue;
        let cableSensorPosition = 1;
        const cableSensors = nextSensors
            .filter((s) => s.cableId === cable.id)
            .sort((a, b) => (a.position ?? a.number ?? 0) - (b.position ?? b.number ?? 0));
        for (const sensor of cableSensors) {
            renumberedSensors.push({
                ...sensor,
                number: sensorNumber,
                position: cableSensorPosition,
                cableId: cable.id,
                cableName: cable.name,
                alternateCableId: cable.id,
                alternateCableName: cable.name,
                binName: cable.binName,
                sectionId: sensor.sectionId || cable.sectionId || 0,
                sectionName: sensor.sectionName || cable.sectionName || "",
                deviceUniqueId: sensor.deviceUniqueId || cable.deviceUniqueId || 0,
                deviceIp: sensor.deviceIp || cable.ipAddress || "",
                type: fitBtsSensorType(sensor.type, cable.cableType),
                name: `${cable.name}-${cableSensorPosition}`,
            });
            sensorNumber += 1;
            cableSensorPosition += 1;
        }
    }

    const restoredHumiditySensors = humiditySensors
        .filter((s) => humiditySensorBelongsToSite(s, binList, cableList))
        .map((s) => attachHumiditySensorToBin(s, binList));

    return [...renumberedSensors, ...restoredHumiditySensors];
}

export function nextTempSetupId(rows) {
    let minId = 0;
    for (const row of rows ?? []) {
        if (typeof row?.id === "number" && row.id < minId) minId = row.id;
    }
    return minId - 1;
}

export function sortSectionPositionRefs(sections, sectionPositionRefs) {
    const sectionOrder = new Map(
        (sections ?? []).map((s) => [String(s.id), s.number ?? 0])
    );
    return [...(sectionPositionRefs ?? [])].sort((a, b) => {
        const ao = sectionOrder.get(String(a.sectionId)) ?? 9999;
        const bo = sectionOrder.get(String(b.sectionId)) ?? 9999;
        if (ao !== bo) return ao - bo;
        return (a.sectionPosition ?? 0) - (b.sectionPosition ?? 0);
    });
}

/** BTS Section.SlotsUsed; fall back to Slots when Used is 0 (common on loaded sites). */
export function effectiveSectionSlots(section, sectionPositionRefs) {
    const used = Number(section?.slotsUsed);
    const slots = Number(section?.slots);
    if (Number.isFinite(used) && used > 0) return used;
    if (Number.isFinite(slots) && slots > 0) return slots;
    const refCount = (sectionPositionRefs ?? []).filter((r) =>
        idsMatch(r.sectionId, section?.id)
    ).length;
    if (refCount > 0) return refCount;
    return 0;
}

/** Slot count for assign — section Slots/SlotsUsed only; never infer from ref row count. */
function effectiveSectionSlotsForAssign(section) {
    const used = Number(section?.slotsUsed);
    const slots = Number(section?.slots);
    if (Number.isFinite(used) && used > 0) return used;
    if (Number.isFinite(slots) && slots > 0) return slots;
    return 6;
}

/** Undo prior auto-expand corruption (slots inflated to cableCount / sectionCount). */
function normalizeSectionsForAssign(sections, cableCount) {
    const list = sections ?? [];
    if (!list.length || cableCount <= 0) return list;
    const inflated = cableCount / list.length;
    if (!Number.isFinite(inflated) || inflated <= 6 || !Number.isInteger(inflated)) {
        return list;
    }
    const allInflated = list.every(
        (s) => effectiveSectionSlotsForAssign(s) === inflated
    );
    if (!allInflated) return list;
    return list.map((s) => ({ ...s, slots: 6, slotsUsed: 6 }));
}

/** CPS for a section slot; default 1 when ref/section CPS is 0 (1 cable per slot). */
export function effectiveCablesPerSlot(ref, section) {
    const refCps = Number(ref?.cablesPerSlot);
    if (Number.isFinite(refCps) && refCps > 0) return refCps;
    const sectionCps = Number(section?.cablesPerSlot);
    if (Number.isFinite(sectionCps) && sectionCps > 0) return sectionCps;
    return 1;
}

/**
 * Build/normalize section slot refs before assign (positions 1..N, CPS ≥ 1).
 * When slot/CPS are 0 in the DB, infer from section Slots + CablesPerSlot.
 */
export function prepareSectionPositionRefsForAssign(sections, sectionPositionRefs) {
    const sortedSections = [...(sections ?? [])].sort(
        (a, b) => (a.number ?? 0) - (b.number ?? 0)
    );
    let refs = [...(sectionPositionRefs ?? [])];

    for (const section of sortedSections) {
        const slotCount = effectiveSectionSlotsForAssign(section);
        if (slotCount <= 0) continue;
        refs = syncSectionPositionRefsForSection(
            { ...section, slots: slotCount, slotsUsed: slotCount },
            refs
        );
    }

    const sectionIds = new Set(sortedSections.map((s) => String(s.id)));
    refs = refs.filter((r) => sectionIds.has(String(r.sectionId)));
    return sortSectionPositionRefs(sections, refs);
}

/** Add/remove SectionPositionRef rows when section slot count changes (BTS Setup slots used). */
export function syncSectionPositionRefsForSection(section, sectionPositionRefs) {
    const sectionId = section.id;
    const targetSlots = effectiveSectionSlots(section, sectionPositionRefs);
    const other = (sectionPositionRefs ?? []).filter((r) => !idsMatch(r.sectionId, sectionId));
    const existing = (sectionPositionRefs ?? []).filter((r) => idsMatch(r.sectionId, sectionId));
    const kept = [];
    let idPool = [...other, ...existing];

    for (let pos = 1; pos <= targetSlots; pos += 1) {
        const prev = existing.find((r) => r.sectionPosition === pos);
        const row = {
            id: prev?.id ?? nextTempSetupId(idPool),
            sectionId,
            sectionPosition: pos,
            cablesPerSlot: effectiveCablesPerSlot(prev, section),
        };
        kept.push(row);
        idPool = [...idPool, row];
    }

    return [...other, ...kept];
}

export function syncAllSectionPositionRefs(sections, sectionPositionRefs) {
    let refs = [...(sectionPositionRefs ?? [])];
    for (const section of sections ?? []) {
        refs = syncSectionPositionRefsForSection(section, refs);
    }
    const sectionIds = new Set((sections ?? []).map((s) => String(s.id)));
    refs = refs.filter((r) => sectionIds.has(String(r.sectionId)));
    return sortSectionPositionRefs(sections, refs);
}

/** Propagate section CPS to every slot ref (BTS txt2 SourceUpdated). */
export function applySectionCablesPerSlotToRefs(sectionId, cablesPerSlot, sectionPositionRefs) {
    const cps = Math.max(1, Number(cablesPerSlot) || 1);
    return (sectionPositionRefs ?? []).map((r) =>
        idsMatch(r.sectionId, sectionId) ? { ...r, cablesPerSlot: cps } : r
    );
}

/** Cables in bin order, then cable order within each bin (BTS assign-sections sort). */
function sortedCablesForAssign(bins, cables) {
    const result = [];
    const included = new Set();

    for (const bin of orderedBins(bins)) {
        for (const cable of cablesForBin(cables, bin.binId)) {
            result.push(cable);
            included.add(String(cable.id));
        }
    }

    for (const cable of cables ?? []) {
        if (!included.has(String(cable.id))) {
            result.push(cable);
        }
    }

    return result;
}

/** Section refs in section number order, then slot position (BTS Setup assign). */
function buildSortedSectionPositionRefs(sections, sectionPositionRefs) {
    const sortedSections = [...(sections ?? [])].sort(
        (a, b) => (a.number ?? 0) - (b.number ?? 0)
    );
    const sectionOrder = new Map(
        sortedSections.map((s) => [String(s.id), s.number ?? 0])
    );
    return [...(sectionPositionRefs ?? [])]
        .filter((r) => sectionOrder.has(String(r.sectionId)))
        .sort((a, b) => {
            const ao = sectionOrder.get(String(a.sectionId)) ?? 9999;
            const bo = sectionOrder.get(String(b.sectionId)) ?? 9999;
            if (ao !== bo) return ao - bo;
            return (a.sectionPosition ?? 0) - (b.sectionPosition ?? 0);
        });
}

function findSectionById(sections, sectionId) {
    return (sections ?? []).find((s) => idsMatch(s.id, sectionId)) ?? null;
}

/** Total cables that can be assigned across all section slots (sum of CPS per slot). */
export function totalSectionAssignCapacity(sections, sectionPositionRefs) {
    const prepared = prepareSectionPositionRefsForAssign(sections, sectionPositionRefs);
    const sortedSections = [...(sections ?? [])].sort(
        (a, b) => (a.number ?? 0) - (b.number ?? 0)
    );
    const sortedRefs = buildSortedSectionPositionRefs(sortedSections, prepared);
    let total = 0;
    for (const ref of sortedRefs) {
        const section = findSectionById(sortedSections, ref.sectionId);
        total += effectiveCablesPerSlot(ref, section);
    }
    return total;
}

/** Duplicate cable ids (e.g. 0) collapse assign updates — give every row a distinct id. */
function ensureUniqueCableIdsForAssign(cables, sensors) {
    const used = new Set();
    let nextTemp = -1;
    const idMap = new Map();

    const nextCables = (cables ?? []).map((cable) => {
        let id = cable.id;
        const key = id == null ? "__null__" : String(id);
        if (id == null || id === 0 || used.has(key)) {
            while (used.has(String(nextTemp))) nextTemp -= 1;
            if (id != null && id !== 0) idMap.set(key, nextTemp);
            id = nextTemp;
            nextTemp -= 1;
        }
        used.add(String(id));
        if (!idMap.has(key)) idMap.set(key, id);
        return { ...cable, id };
    });

    const nextSensors = (sensors ?? []).map((sensor) => {
        if (sensor.cableId == null) return sensor;
        const mapped = idMap.get(String(sensor.cableId));
        return mapped != null ? { ...sensor, cableId: mapped } : sensor;
    });

    return { cables: nextCables, sensors: nextSensors };
}

/** Pig-tail start within a section slot (1-based): cable 1 → 1, cable 2 → 1+sensors, etc. */
function sectionPositionStartAtForSlotCable(slotCableIndex, sensorCount) {
    const sensors = Math.max(1, Number(sensorCount) || 1);
    const slotCable = Math.max(1, Number(slotCableIndex) || 1);
    return 1 + sensors * (slotCable - 1);
}

function clearCableSectionAssignment(cable, sensors, sensorById) {
    cable.sectionId = 0;
    cable.sectionName = "";
    cable.deviceUniqueId = 0;
    cable.ipAddress = "";
    cable.sectionPosition = 0;
    cable.sectionPositionStartAt = 0;
    for (const sensor of sensors ?? []) {
        if (!idsMatch(sensor.cableId, cable.id) || isHumiditySensor(sensor)) continue;
        const s = sensorById.get(String(sensor.id));
        if (s) {
            s.deviceUniqueId = 0;
            s.deviceIp = "";
            s.sectionName = "";
            s.sectionId = 0;
        }
    }
}

/** Assign cables/sensors to sections in cable order (BTS btnAssignSections). */
export function assignSectionsToCables(sections, sectionPositionRefs, bins, cables, sensors) {
    const cableSync = syncCablesFromBins(bins, cables, sections, sensors);
    const uniqueIds = ensureUniqueCableIdsForAssign(cableSync.cables, cableSync.sensors);
    const normalizedCables = uniqueIds.cables;
    const normalizedSensors = uniqueIds.sensors;
    const sortedCables = sortedCablesForAssign(bins, normalizedCables);
    const workingSections = normalizeSectionsForAssign(sections, sortedCables.length);

    const preparedRefs = prepareSectionPositionRefsForAssign(workingSections, sectionPositionRefs);
    const sortedSections = [...workingSections].sort(
        (a, b) => (a.number ?? 0) - (b.number ?? 0)
    );
    const sortedRefs = buildSortedSectionPositionRefs(sortedSections, preparedRefs);

    const cableById = new Map(normalizedCables.map((c) => [String(c.id), { ...c }]));
    const sensorById = new Map(normalizedSensors.map((s) => [String(s.id), { ...s }]));

    for (const cable of cableById.values()) {
        if (isHumidityOnlyCable(cable, normalizedSensors)) continue;
        clearCableSectionAssignment(cable, normalizedSensors, sensorById);
    }

    let refIdx = 0;
    let slotCable = 1;

    for (const sortedCable of sortedCables) {
        const cable = cableById.get(String(sortedCable.id));
        if (!cable) continue;
        if (isHumidityOnlyCable(cable, normalizedSensors)) continue;

        if (refIdx >= sortedRefs.length) {
            clearCableSectionAssignment(cable, normalizedSensors, sensorById);
            continue;
        }

        const spr = sortedRefs[refIdx];
        const section = findSectionById(sortedSections, spr.sectionId);
        const cps = effectiveCablesPerSlot(spr, section);

        if (section != null && slotCable <= cps) {
            const slotPosition = spr.sectionPosition > 0 ? spr.sectionPosition : slotCable;
            cable.sectionId = section.id;
            cable.sectionName = section.name;
            cable.deviceUniqueId = section.deviceUniqueId;
            cable.ipAddress = section.ipAddress;
            cable.sectionPosition = slotPosition;
            cable.sectionPositionStartAt = sectionPositionStartAtForSlotCable(
                slotCable,
                cable.sensorCount
            );

            for (const sensor of normalizedSensors ?? []) {
                if (!idsMatch(sensor.cableId, cable.id) || isHumiditySensor(sensor)) continue;
                const s = sensorById.get(String(sensor.id));
                if (s) {
                    s.deviceUniqueId = section.deviceUniqueId;
                    s.deviceIp = section.ipAddress;
                    s.sectionName = section.name;
                    s.sectionId = section.id;
                }
            }

            slotCable += 1;
            if (slotCable > cps) {
                refIdx += 1;
                slotCable = 1;
            }
        }
    }

    return {
        sections: workingSections,
        cables: normalizedCables.map((c) => cableById.get(String(c.id)) ?? c),
        sensors: normalizedSensors.map((s) => sensorById.get(String(s.id)) ?? s),
        sectionPositionRefs: preparedRefs,
    };
}

/** Rebuild dependent rows when a setup section is expanded (mirrors BTS Draw* on group open). */
export function refreshSetupSection(sectionId, setup) {
    if (!setup) return setup;
    switch (sectionId) {
        case "bins": {
            const bins = applyBinCableLayouts(setup.bins);
            const { cables, sensors } = syncCablesFromBins(bins, setup.cables, setup.sections, setup.sensors);
            const fansAttached = attachFansToBins(setup.fans, bins);
            const binsWithFanCounts = updateBinFanCounts(bins, fansAttached);
            return {
                ...setup,
                bins: binsWithFanCounts,
                cables,
                sensors,
                fans: syncFansFromBins(binsWithFanCounts, fansAttached, setup.devices),
            };
        }
        case "fans": {
            const fansAttached = attachFansToBins(setup.fans, setup.bins);
            const bins = updateBinFanCounts(setup.bins, fansAttached);
            return { ...setup, bins, fans: syncFansFromBins(bins, fansAttached, setup.devices) };
        }
        case "cables": {
            const { cables, sensors } = syncCablesFromBins(setup.bins, setup.cables, setup.sections, setup.sensors);
            return { ...setup, cables, sensors };
        }
        case "sensors": {
            const { cables, sensors } = syncCablesFromBins(setup.bins, setup.cables, setup.sections, setup.sensors);
            return {
                ...setup,
                cables,
                sensors: syncSensorsFromCables(cables, sensors, setup.bins),
            };
        }
        case "sections": {
            const sectionPositionRefs = syncAllSectionPositionRefs(
                setup.sections,
                setup.sectionPositionRefs
            );
            return { ...setup, sectionPositionRefs };
        }
        default:
            return setup;
    }
}

/** Ensure each entity row has a distinct temp ID before save (sync can create duplicates). */
export function ensureUniqueSetupIds(setup) {
    if (!setup) return setup;

    const allIds = [];
    const collect = (arr, ...keys) => {
        (arr || []).forEach((row) => {
            keys.forEach((key) => {
                const id = row?.[key];
                if (typeof id === "number") allIds.push(id);
            });
        });
    };
    collect(setup.devices, "uniqueId");
    collect(setup.modules, "id");
    collect(setup.sections, "id");
    collect(setup.sectionPositionRefs, "id");
    collect(setup.bins, "binId");
    collect(setup.fans, "fanId");
    collect(setup.cables, "id");
    collect(setup.sensors, "id");

    const used = new Set();
    let nextTemp = -1;
    const negatives = allIds.filter((id) => id < 0);
    if (negatives.length) nextTemp = Math.min(...negatives) - 1;

    const register = (id) => {
        if (typeof id === "number" && id !== 0 && !used.has(id)) {
            used.add(id);
            return id;
        }
        while (used.has(nextTemp)) nextTemp -= 1;
        const assigned = nextTemp;
        nextTemp -= 1;
        used.add(assigned);
        return assigned;
    };

    const binIdMap = new Map();
    const bins = (setup.bins || []).map((b) => {
        const oldId = b.binId;
        const binId = register(b.binId);
        if (oldId !== binId) binIdMap.set(oldId, binId);
        return { ...b, binId };
    });

    const mapBinRef = (row) => {
        if (!row || row.binId == null) return row;
        const mapped = binIdMap.get(row.binId);
        if (mapped == null) return row;
        return { ...row, binId: mapped };
    };

    const cableIdMap = new Map();

    const mapCableRef = (row) => {
        if (isHumiditySensor(row) && (row.cableId == null || row.cableId === 0)) return row;
        const mapped = cableIdMap.get(row.cableId);
        if (mapped == null) return row;
        if (isHumiditySensor(row)) return { ...row, cableId: mapped };
        return {
            ...row,
            cableId: mapped,
            alternateCableId: mapped,
        };
    };

    const sectionIdMap = new Map();
    const sections = (setup.sections || []).map((s) => {
        const oldId = s.id;
        const id = register(s.id);
        if (oldId !== id) sectionIdMap.set(oldId, id);
        return { ...s, id };
    });

    const mapSectionRef = (row) => {
        if (!row || row.sectionId == null) return row;
        const mapped = sectionIdMap.get(row.sectionId);
        if (mapped == null) return row;
        return { ...row, sectionId: mapped };
    };

    const mapCableSectionFields = (row) => {
        const mapped = mapSectionRef(row);
        if (!mapped.sectionId) return mapped;
        const section = sections.find((s) => s.id === mapped.sectionId);
        if (!section) return mapped;
        return {
            ...mapped,
            sectionName: section.name,
            deviceUniqueId: section.deviceUniqueId,
            ipAddress: section.ipAddress,
        };
    };

    return {
        ...setup,
        devices: (setup.devices || []).map((d) => {
            const uniqueId = register(d.uniqueId);
            return { ...d, uniqueId, id: d.id > 0 ? d.id : uniqueId };
        }),
        modules: (setup.modules || []).map((m) => ({ ...m, id: register(m.id) })),
        sections,
        sectionPositionRefs: (setup.sectionPositionRefs || []).map((spr) =>
            mapSectionRef({ ...spr, id: register(spr.id) })
        ),
        bins,
        fans: (setup.fans || []).map((f) => mapBinRef({ ...f, fanId: register(f.fanId) })),
        cables: (setup.cables || []).map((c) => {
            const oldId = c.id;
            const id = register(c.id);
            if (oldId !== id) cableIdMap.set(oldId, id);
            return mapCableSectionFields(mapBinRef({ ...c, id }));
        }),
        sensors: (setup.sensors || []).map((s) => {
            const mapped = mapCableRef(mapBinRef(mapSectionRef(s)));
            return { ...mapped, id: register(mapped.id) };
        }),
    };
}
